if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Vertical thumbnail scroll arrow controller for desktop
        const prevButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');
        const list = this.elements.thumbnails.querySelector('.thumbnail-list');
        
        if (prevButton && nextButton && list) {
          prevButton.addEventListener('click', (e) => {
            if (window.innerWidth >= 750) {
              e.preventDefault();
              e.stopPropagation();
              list.scrollBy({ top: -120, behavior: 'smooth' });
            }
          });
          nextButton.addEventListener('click', (e) => {
            if (window.innerWidth >= 750) {
              e.preventDefault();
              e.stopPropagation();
              list.scrollBy({ top: 120, behavior: 'smooth' });
            }
          });

          // Premium dynamic arrow scroll indicator states
          const updateScrollButtons = () => {
            if (window.innerWidth < 750) {
              // Reset mobile styles
              prevButton.style.display = '';
              nextButton.style.display = '';
              prevButton.style.opacity = '';
              prevButton.style.pointerEvents = '';
              nextButton.style.opacity = '';
              nextButton.style.pointerEvents = '';
              return;
            }
            
            const isScrollable = list.scrollHeight > list.clientHeight;
            if (!isScrollable) {
              prevButton.style.display = 'none';
              nextButton.style.display = 'none';
              return;
            } else {
              prevButton.style.display = 'flex';
              nextButton.style.display = 'flex';
            }
            
            const scrollTop = list.scrollTop;
            const maxScrollTop = list.scrollHeight - list.clientHeight;
            
            // UP button state
            if (scrollTop <= 2) {
              prevButton.setAttribute('disabled', 'true');
              prevButton.style.opacity = '0.15';
              prevButton.style.pointerEvents = 'none';
            } else {
              prevButton.removeAttribute('disabled');
              prevButton.style.opacity = '1';
              prevButton.style.pointerEvents = 'auto';
            }
            
            // DOWN button state
            if (scrollTop >= maxScrollTop - 2) {
              nextButton.setAttribute('disabled', 'true');
              nextButton.style.opacity = '0.15';
              nextButton.style.pointerEvents = 'none';
            } else {
              nextButton.removeAttribute('disabled');
              nextButton.style.opacity = '1';
              nextButton.style.pointerEvents = 'auto';
            }
          };

          // Attach scroll event and resize listeners
          list.addEventListener('scroll', updateScrollButtons);
          window.addEventListener('resize', updateScrollButtons);
          
          // Initial trigger after layout has rendered
          setTimeout(updateScrollButtons, 300);
          this.updateScrollButtons = updateScrollButtons; // Save reference for other triggers
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Center active thumbnail vertically on desktop
        if (window.innerWidth >= 750) {
          const list = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (list) {
            const listRect = list.getBoundingClientRect();
            const thumbRect = thumbnail.getBoundingClientRect();
            
            // Calculate scroll target so that the thumbnail is centered in the list container
            const scrollTopTarget = list.scrollTop + (thumbRect.top - listRect.top) - (listRect.height / 2) + (thumbRect.height / 2);
            list.scrollTo({ top: scrollTopTarget, behavior: 'smooth' });
            
            // Trigger button state update
            if (this.updateScrollButtons) {
              setTimeout(this.updateScrollButtons, 100);
            }
          }
        } else {
          // Fallback to standard horizontal sliding on mobile
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
